import { useState } from 'react';
import { EmailTemplate } from '../../../mocks/emails';
import { bookingsData } from '../../../mocks/bookings';
import { agencyInfo } from '../../../mocks/dashboard';

interface EmailComposerProps {
  template: EmailTemplate | null;
  onClose: () => void;
  onSent: (toName: string, subject: string) => void;
}

const attachmentOptions: Record<string, string[]> = {
  booking_confirmation: ['Booking Summary PDF', 'Itinerary PDF', 'Invoice PDF'],
  invoice: ['Invoice PDF', 'Payment Receipt PDF'],
  itinerary: ['Itinerary PDF', 'Destination Guide PDF'],
  payment_receipt: ['Payment Receipt PDF'],
  voucher: ['Service Voucher PDF'],
  follow_up: ['Travel Tips PDF', 'Feedback Form PDF'],
  custom: ['Booking Summary PDF', 'Invoice PDF', 'Itinerary PDF', 'Voucher PDF'],
};

export default function EmailComposer({ template, onClose, onSent }: EmailComposerProps) {
  const brand = agencyInfo.brandColor;
  const [step, setStep] = useState(1);
  const [selectedBooking, setSelectedBooking] = useState('');
  const [toEmail, setToEmail] = useState('');
  const [toName, setToName] = useState('');
  const [subject, setSubject] = useState(template?.subject || '');
  const [body, setBody] = useState('');
  const [selectedAttachments, setSelectedAttachments] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const attachOpts = attachmentOptions[template?.type || 'custom'];

  const handleBookingSelect = (bookingId: string) => {
    setSelectedBooking(bookingId);
    const booking = bookingsData.find((b) => b.id === bookingId);
    if (booking) {
      setToEmail(booking.customerEmail);
      setToName(booking.customer);
      if (template) {
        const subj = template.subject
          .replace('{{booking_id}}', booking.id)
          .replace('{{destination}}', booking.destination)
          .replace('{{travel_dates}}', booking.travelDate)
          .replace('{{days_away}}', '3')
          .replace('{{invoice_number}}', 'INV-2026-' + booking.id.replace('BK-', ''))
          .replace('{{agency_name}}', agencyInfo.name)
          .replace('{{amount}}', `${booking.currency} ${booking.paidAmount.toLocaleString()}`);
        setSubject(subj);
      }
      const defaultBody = `Dear ${booking.customer},\n\nThank you for choosing ${agencyInfo.name}. Please find the details below.\n\nBooking Reference: ${booking.id}\nDestination: ${booking.destination}\nTravel Date: ${booking.travelDate}\n\nFor any queries, please don't hesitate to contact us.\n\nWarm regards,\n${agencyInfo.name} Team`;
      setBody(defaultBody);
    }
  };

  const toggleAttachment = (att: string) => {
    setSelectedAttachments((prev) =>
      prev.includes(att) ? prev.filter((a) => a !== att) : [...prev, att]
    );
  };

  const handleSend = () => {
    setSending(true);
    setTimeout(() => {
      setSending(false);
      setSent(true);
    }, 1800);
  };

  if (sent) {
    return (
      <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div
          className="bg-white rounded-2xl p-8 max-w-sm w-full text-center"
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
            style={{ backgroundColor: brand + '15' }}
          >
            <i className="ri-send-plane-2-fill text-3xl" style={{ color: brand }} />
          </div>
          <h3 className="font-bold text-gray-800 text-lg mb-1">Email Sent!</h3>
          <p className="text-sm text-gray-500 mb-1">
            Successfully sent to <strong>{toName}</strong>
          </p>
          <p className="text-xs text-gray-400 mb-6 truncate">{toEmail}</p>
          <button
            onClick={() => { onSent(toName, subject); onClose(); }}
            className="w-full py-2.5 rounded-xl text-white text-sm font-medium cursor-pointer"
            style={{ backgroundColor: brand }}
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            {template && (
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: template.bgColor, color: template.color }}
              >
                <i className={`${template.icon} text-base`} />
              </div>
            )}
            <div>
              <p className="font-semibold text-gray-800 text-sm">
                {template ? `Compose: ${template.name}` : 'Compose Email'}
              </p>
              <div className="flex items-center gap-1.5 mt-0.5">
                {[1, 2, 3].map((s) => (
                  <div
                    key={s}
                    className="w-16 h-1 rounded-full transition-colors"
                    style={{ backgroundColor: step >= s ? brand : '#e5e7eb' }}
                  />
                ))}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {/* Step 1: Recipient */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-3">Step 1 — Select Recipient</p>
                <div className="mb-3">
                  <label className="text-xs font-medium text-gray-500 mb-1.5 block">Link to Booking (optional)</label>
                  <select
                    value={selectedBooking}
                    onChange={(e) => handleBookingSelect(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-700 focus:outline-none focus:border-gray-300 cursor-pointer bg-white"
                  >
                    <option value="">— Select a booking to auto-fill —</option>
                    {bookingsData.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.id} — {b.customer} ({b.destination})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-500 mb-1.5 block">Customer Name</label>
                    <input
                      type="text"
                      value={toName}
                      onChange={(e) => setToName(e.target.value)}
                      placeholder="e.g. Ahmed Al-Rashid"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-gray-300"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 mb-1.5 block">Email Address</label>
                    <input
                      type="email"
                      value={toEmail}
                      onChange={(e) => setToEmail(e.target.value)}
                      placeholder="customer@email.com"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-gray-300"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Subject + Body */}
          {step === 2 && (
            <div className="space-y-4">
              <p className="text-sm font-semibold text-gray-700">Step 2 — Email Content</p>
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1.5 block">Subject Line</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-gray-300"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1.5 block">Message Body</label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={10}
                  placeholder="Write your email message here..."
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-gray-300 resize-none"
                />
              </div>
            </div>
          )}

          {/* Step 3: Attachments + Review */}
          {step === 3 && (
            <div className="space-y-5">
              <p className="text-sm font-semibold text-gray-700">Step 3 — Attachments & Review</p>

              {/* Attachments */}
              <div>
                <label className="text-xs font-medium text-gray-500 mb-2 block">Select Attachments</label>
                <div className="grid grid-cols-2 gap-2">
                  {attachOpts.map((att) => (
                    <button
                      key={att}
                      onClick={() => toggleAttachment(att)}
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm text-left transition-all cursor-pointer ${
                        selectedAttachments.includes(att)
                          ? 'border-opacity-100'
                          : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                      style={
                        selectedAttachments.includes(att)
                          ? { borderColor: brand, backgroundColor: brand + '10', color: brand }
                          : {}
                      }
                    >
                      <i
                        className={`${
                          selectedAttachments.includes(att) ? 'ri-checkbox-circle-fill' : 'ri-file-pdf-line'
                        } text-base flex-shrink-0`}
                      />
                      <span className="text-xs truncate">{att}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Summary */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Email Summary</p>
                <div className="flex gap-2">
                  <span className="text-gray-400 w-16 flex-shrink-0">To:</span>
                  <span className="text-gray-700 font-medium">{toName} &lt;{toEmail}&gt;</span>
                </div>
                <div className="flex gap-2">
                  <span className="text-gray-400 w-16 flex-shrink-0">Subject:</span>
                  <span className="text-gray-700">{subject}</span>
                </div>
                <div className="flex gap-2">
                  <span className="text-gray-400 w-16 flex-shrink-0">Files:</span>
                  <span className="text-gray-700">
                    {selectedAttachments.length > 0 ? selectedAttachments.join(', ') : 'No attachments'}
                  </span>
                </div>
                <div className="flex gap-2">
                  <span className="text-gray-400 w-16 flex-shrink-0">From:</span>
                  <span className="text-gray-700">{agencyInfo.name} &lt;travel@horizontravel.ae&gt;</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex justify-between items-center">
          <button
            onClick={step === 1 ? onClose : () => setStep((s) => s - 1)}
            className="px-4 py-2 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
          >
            {step === 1 ? 'Cancel' : 'Back'}
          </button>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">Step {step} of 3</span>
            {step < 3 ? (
              <button
                onClick={() => setStep((s) => s + 1)}
                disabled={step === 1 && (!toEmail || !toName)}
                className="px-4 py-2 text-sm rounded-lg text-white font-medium cursor-pointer whitespace-nowrap disabled:opacity-50"
                style={{ backgroundColor: brand }}
              >
                Continue
              </button>
            ) : (
              <button
                onClick={handleSend}
                disabled={sending || !subject}
                className="px-5 py-2 text-sm rounded-lg text-white font-medium cursor-pointer whitespace-nowrap flex items-center gap-2 disabled:opacity-50"
                style={{ backgroundColor: brand }}
              >
                {sending ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-2-line" />
                    Send Email
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
